'use client';

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/utils/api';

/**
 * Meteo della CER — 14 variabili dalla cache server (INB-36).
 *
 * Prima questo hook interrogava Open-Meteo direttamente dal browser, con tre
 * coordinate cablate qui dentro (nord=Milano, centro=Roma, sud=Napoli): ogni
 * CER del sud vedeva il meteo di Napoli, e un'interruzione dell'API lasciava la
 * pagina senza nulla. Ora legge `/api/weather`, che serve la cache riempita dal
 * giro dei 15 minuti sulla posizione vera della configurazione.
 */

export interface WeatherForecastDay {
	date: string;
	cloudCoverPct: number | null;
	irradianceKwhM2: number | null;
	/** Irraggiamento sul piano moduli — più vicino a ciò che vede l'impianto. */
	tiltedIrradianceKwhM2: number | null;
	tempMinC: number | null;
	tempMaxC: number | null;
	windMaxKmh: number | null;
	precipitationMm: number | null;
	snowfallCm: number | null;
	hours: number;
}

export interface WeatherData {
	temperatureC: number;
	cloudCoverPct: number;
	windSpeedKmh: number;
	humidityPct: number;
	irradianceKwh: number | null;
	/** Irraggiamento orizzontale istantaneo, W/m². */
	ghiWm2: number | null;
	/** Irraggiamento sul piano dei moduli istantaneo, W/m². */
	poaWm2: number | null;
	/** Temperatura di cella stimata col modello NOCT, °C. */
	moduleTempC: number | null;
	label: string;
	forecast: WeatherForecastDay[];
	/** Minuti trascorsi dall'ora del campione servito. */
	ageMinutes: number;
	/** Variabili con almeno un valore in cache, su `variablesTracked`. */
	variablesAvailable: number;
	variablesTracked: number;
}

interface WeatherCurrentDto {
	observedAt: string;
	ageMinutes: number;
	temperatureC: number | null;
	humidityPct: number | null;
	cloudCoverPct: number | null;
	windSpeedKmh: number | null;
	ghiWm2: number | null;
	gtiWm2: number | null;
	moduleTempC: number | null;
}

interface WeatherResponse {
	available: boolean;
	variablesAvailable: number;
	variablesTracked: number;
	current: WeatherCurrentDto | null;
	forecast: WeatherForecastDay[];
	location: { lat: number; lon: number; key: string; from: string };
}

interface UseWeatherReturn {
	weather: WeatherData | null;
	isLoading: boolean;
	error: string | null;
}

/** Etichetta leggibile della posizione servita, es. "41.90°N, 12.49°E". */
function locationLabel(loc: WeatherResponse['location']): string {
	const ns = loc.lat >= 0 ? 'N' : 'S';
	const ew = loc.lon >= 0 ? 'E' : 'O';
	return `${Math.abs(loc.lat).toFixed(2)}°${ns}, ${Math.abs(loc.lon).toFixed(2)}°${ew}`;
}

function toWeatherData(data: WeatherResponse): WeatherData | null {
	if (!data?.available || !data.current) return null;
	const cur = data.current;
	return {
		temperatureC: Math.round(cur.temperatureC ?? 0),
		cloudCoverPct: Math.round(cur.cloudCoverPct ?? 0),
		windSpeedKmh: Math.round(cur.windSpeedKmh ?? 0),
		humidityPct: Math.round(cur.humidityPct ?? 0),
		irradianceKwh: data.forecast[0]?.irradianceKwhM2 ?? null,
		// Le istantanee restano `null` quando la cache non le ha: a differenza
		// delle correnti sopra non si arrotondano a zero, perché 0 W/m² è un
		// valore legittimo (notte) e sarebbe indistinguibile da «non so».
		ghiWm2: cur.ghiWm2 === null ? null : Math.round(cur.ghiWm2),
		poaWm2: cur.gtiWm2 === null ? null : Math.round(cur.gtiWm2),
		moduleTempC: cur.moduleTempC === null ? null : Math.round(cur.moduleTempC),
		label: locationLabel(data.location),
		forecast: data.forecast,
		ageMinutes: cur.ageMinutes,
		variablesAvailable: data.variablesAvailable,
		variablesTracked: data.variablesTracked,
	};
}

/** Cadenza di rinfresco lato client, allineata al cron dei 15 minuti. */
const POLL_MS = 15 * 60 * 1000;

/**
 * ADR-006: backed by React Query — cache/dedup per scope, `refetchInterval`
 * allineato al cron dei 15 minuti.
 */
export function useWeatherData(orgId?: string | null): UseWeatherReturn {
	const { data, isLoading, error } = useQuery({
		queryKey: ['weather', orgId],
		enabled: !!orgId,
		refetchInterval: POLL_MS,
		queryFn: () => api.get<WeatherResponse>('/api/weather', { orgId: orgId as string }),
		select: toWeatherData,
	});

	return {
		weather: data ?? null,
		isLoading: orgId ? isLoading : false,
		error: error ? (error instanceof Error ? error.message : 'Errore meteo') : null,
	};
}
